import { Button as IconButton } from '@/components/ui/button'
import { centsToEuros } from '@/constants'
import { useAuth } from '@/lib'
import { logger } from '@/ui'
import { Checkbox } from '@headlessui/react'
import {
	customerWalletShowQueryKey,
	inventoryIndexQueryKey,
	type InventoryItemData,
	type InventorySplitRequest,
	type InventoryStoreRequest,
	locationEnum as location,
	useCatalogueItemIndex,
	useCustomFieldList,
	useInventoryConvert,
	useInventoryDestroy,
	useInventoryIndex,
	useInventorySplit,
	useInventoryStore,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { SplitIcon, Trash2Icon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { appendToastAtom, useToastStoreContext } from '../../../..'
import { Button, Tooltip } from '../../../../rapid-ui'
import { ConfirmationDialog, type ConfirmationDialogRef } from '../../../../rapid-ui/structural'
import { CreateInventoryDialog } from './CreateInventoryDialog'
import { InventoryConversionDialog } from './InventoryConversionDialog'
import { SplitInventoryDialog } from './SplitInventoryDialog'

interface InventorySectionProps {
	organisationId: string
	openCreateRef: { current: (() => void) | null }
}

export function InventorySection({ organisationId, openCreateRef }: InventorySectionProps) {
	const { t } = useTranslation('common')
	const { can } = useAuth()
	const canAddItem = can('inventory:add-item')
	const canDelete = can('inventory:delete')
	const canSplit = can('inventory:split')
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [showCreateDialog, setShowCreateDialog] = useState(false)
	const [splittingItem, setSplittingItem] = useState<InventoryItemData | null>(null)
	const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({})
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
	const deleteDialogRef = useRef<ConfirmationDialogRef>(null)
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })

	// The page header's "Add item" button opens this section's create dialog.
	useEffect(() => {
		openCreateRef.current = () => setShowCreateDialog(true)
	}, [openCreateRef])

	const queryClient = useQueryClient()
	const invalidateInventory = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: inventoryIndexQueryKey(organisationId) })
	}, [queryClient, organisationId])

	const { data, isLoading, isError, error } = useInventoryIndex(organisationId)
	const { data: catalogueData } = useCatalogueItemIndex()
	const convertMutation = useInventoryConvert({
		onSuccess: () => {
			invalidateInventory()
			void queryClient.invalidateQueries({ queryKey: customerWalletShowQueryKey(organisationId) })
		},
	})
	const createMutation = useInventoryStore({ onSuccess: invalidateInventory })
	const deleteMutation = useInventoryDestroy({ onSuccess: invalidateInventory })
	const splitMutation = useInventorySplit({ onSuccess: invalidateInventory })
	const invoiceCustomFields = useCustomFieldList(organisationId, location.invoice)
	const profilesQuery = useProfilesIndex(organisationId, {
		enabled: !!organisationId && can('profile:view'),
	})

	const customFields = invoiceCustomFields.data?.fields ?? []
	const billingProfiles = profilesQuery.data?.data ?? []

	// Default the billed profile to the organisation's primary one once profiles load.
	useEffect(() => {
		if (selectedProfileId !== null || billingProfiles.length === 0) { return }
		const primaryProfile = billingProfiles.find((profile) => profile.isPrimary)
		setSelectedProfileId((primaryProfile ?? billingProfiles[0]).id)
	}, [billingProfiles, selectedProfileId])

	const setCustomFieldValue = useCallback((fieldId: string, value: string) => {
		setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }))
	}, [])

	const catalogueItems = catalogueData?.data ?? []

	const inventoryItems = data?.data ?? []
	const selectableItems = inventoryItems.filter((item) => item.availableQuantity === item.quantity)
	const hasItems = inventoryItems.length > 0
	const hasSelection = selectedIds.size > 0

	const selectedItems = inventoryItems.filter((item) => selectedIds.has(item.id))
	const totalSelectedValue = selectedItems.reduce((sum, item) => sum + (item.valueCents * item.quantity), 0)
	const totalSelectedQuantity = selectedItems.reduce((sum, item) => sum + item.quantity, 0)

	const toggleItem = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}
			return next
		})
	}

	const toggleAll = () => {
		if (selectedIds.size === selectableItems.length) {
			setSelectedIds(new Set())
		} else {
			setSelectedIds(new Set(selectableItems.map((item) => item.id)))
		}
	}

	const handleConvert = async () => {
		try {
			const filledCustomFields = Object.entries(customFieldValues)
				.filter(([, value]) => value.trim() !== '')
				.map(([id, value]) => ({ id, value }))

			await convertMutation.mutateAsync({
				organisation: organisationId,
				data: {
					inventoryItemIds: Array.from(selectedIds),
					...(filledCustomFields.length > 0 && { customFields: filledCustomFields }),
					...(selectedProfileId && { billingProfileId: selectedProfileId }),
				},
			})
			setSelectedIds(new Set())
			setCustomFieldValues({})
			setShowConfirmDialog(false)
		} catch (err) {
			// Error is handled in the dialog
		}
	}

	const handleCreate = async (inventoryData: InventoryStoreRequest) => {
		try {
			await createMutation.mutateAsync({ organisation: organisationId, data: inventoryData })
			setShowCreateDialog(false)
		} catch (err) {
			// Error is handled in the dialog
		}
	}

	const handleSplitConfirm = async (
		{ currentQuantity, splits }: { currentQuantity: number; splits: InventorySplitRequest['splits'] },
	) => {
		if (!splittingItem) { return }
		try {
			await splitMutation.mutateAsync({
				organisation: organisationId,
				item: splittingItem.id,
				data: { currentQuantity, splits },
			})
			setSplittingItem(null)
		} catch (err) {
			logger.error(err, {
				tags: { component: 'InventorySection', action: 'split' },
				data: {
					organisation_id: organisationId,
					inventory_item_id: splittingItem.id,
					item_quantity: splittingItem.quantity,
					current_quantity: currentQuantity,
					split_count: splits.length,
				},
			})
		}
	}

	const handleDelete = async (item: InventoryItemData) => {
		try {
			await deleteDialogRef.current?.confirm({
				title: t('inventory.delete.title'),
				message: t('inventory.delete.message', {
					amount: centsToEuros(item.valueCents).toFixed(2),
					count: item.quantity,
				}),
				confirmText: t('inventory.delete.confirm'),
				cancelText: t('inventory.delete.cancel'),
			})
		} catch {
			return
		}

		try {
			await deleteMutation.mutateAsync({ organisation: organisationId, item: item.id })
			setSelectedIds((prev) => {
				if (!prev.has(item.id)) { return prev }
				const next = new Set(prev)
				next.delete(item.id)
				return next
			})
		} catch (err) {
			appendToast({
				title: t('inventory.delete.failed'),
				description: err instanceof Error ? err.message : undefined,
				variant: 'error',
				cause: err,
			})
		}
	}

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="text-sm text-gray-500">{t('inventory.loading')}</div>
			</div>
		)
	}

	if (isError) {
		return (
			<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
				<div className="font-medium text-red-900">{t('inventory.errorLoad')}</div>
				<div className="text-red-700">{(error as Error)?.message || t('common.tryAgain')}</div>
			</div>
		)
	}

	if (!hasItems) {
		return (
			<>
				<div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
					<div className="text-gray-500">{t('inventory.empty')}</div>
					<div className="text-sm text-gray-400 mt-1">
						{t('inventory.emptyDescription')}
					</div>
				</div>
				{canAddItem && (
					<CreateInventoryDialog
						open={showCreateDialog}
						onClose={() => setShowCreateDialog(false)}
						onSubmit={handleCreate}
						catalogueItems={catalogueItems}
						isLoading={createMutation.isPending}
						error={createMutation.error}
					/>
				)}
			</>
		)
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-gray-500">
				{t('inventory.description')}
			</p>

			<div className="flex items-center justify-between">
				<button
					type="button"
					onClick={toggleAll}
					className="flex items-center gap-2 cursor-pointer text-sm"
				>
					<span className="pointer-events-none">
						<ItemCheckbox
							checked={selectableItems.length > 0 && selectedIds.size === selectableItems.length}
							indeterminate={selectedIds.size > 0 && selectedIds.size < selectableItems.length}
							onChange={toggleAll}
						/>
					</span>
					<span className="text-gray-600">
						{selectedIds.size === 0
							? t('inventory.selectAll')
							: t('inventory.selectedCount', {
								selected: selectedIds.size,
								total: selectableItems.length,
							})}
					</span>
				</button>

				{hasSelection && (
					<div className="text-sm text-gray-600">
						{t('inventory.totalSummary', {
							amount: centsToEuros(totalSelectedValue).toFixed(2),
							count: totalSelectedQuantity,
						})}
					</div>
				)}
			</div>

			<div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
				{inventoryItems.map((item) => (
					<InventoryItem
						key={item.id}
						item={item}
						selected={selectedIds.has(item.id)}
						onToggle={() => toggleItem(item.id)}
						onDelete={canDelete ? () => handleDelete(item) : undefined}
						onSplit={canSplit ? () => setSplittingItem(item) : undefined}
					/>
				))}
			</div>

			<div className="flex items-center gap-4">
				<Button
					type="committal"
					onClick={() => setShowConfirmDialog(true)}
					disabled={!hasSelection}
				>
					{t('inventory.convertToCredits')}
				</Button>

				{hasSelection && (
					<span className="text-sm text-gray-500">
						{t('inventory.invoiceHint', { amount: centsToEuros(totalSelectedValue).toFixed(2) })}
					</span>
				)}
			</div>

			<InventoryConversionDialog
				open={showConfirmDialog}
				onClose={() => setShowConfirmDialog(false)}
				onConfirm={handleConvert}
				selectedItems={selectedItems}
				totalValue={totalSelectedValue}
				totalQuantity={totalSelectedQuantity}
				isLoading={convertMutation.isPending}
				error={convertMutation.error}
				customFields={customFields}
				customFieldValues={customFieldValues}
				onCustomFieldChange={setCustomFieldValue}
				billingProfiles={billingProfiles}
				selectedProfileId={selectedProfileId}
				onProfileChange={setSelectedProfileId}
			/>

			{canAddItem && (
				<CreateInventoryDialog
					open={showCreateDialog}
					onClose={() => setShowCreateDialog(false)}
					onSubmit={handleCreate}
					catalogueItems={catalogueItems}
					isLoading={createMutation.isPending}
					error={createMutation.error}
				/>
			)}

			<ConfirmationDialog ref={deleteDialogRef} />

			{canSplit && (
				<SplitInventoryDialog
					open={splittingItem !== null}
					item={splittingItem}
					onClose={() => {
						if (splitMutation.isPending) { return }
						setSplittingItem(null)
						splitMutation.reset()
					}}
					onConfirm={handleSplitConfirm}
					catalogueItems={catalogueItems}
					isLoading={splitMutation.isPending}
					error={splitMutation.error}
				/>
			)}
		</div>
	)
}

interface InventoryItemProps {
	item: InventoryItemData
	selected: boolean
	onToggle: () => void
	onDelete?: () => void
	onSplit?: () => void
}

function InventoryItem({ item, selected, onToggle, onDelete, onSplit }: InventoryItemProps) {
	const { t } = useTranslation('common')
	const expiresAt = new Date(item.expiresAt)
	const isExpiringSoon = !item.isExpired && expiresAt.getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000 // 30 days
	const heldQuantity = item.quantity - item.availableQuantity
	const isHeld = heldQuantity > 0

	return (
		<div
			className={ctl(`
				group flex items-center gap-4 p-4 transition-colors
				${selected ? 'bg-brand-50' : 'bg-white hover:bg-gray-50'}
			`)}
		>
			<label
				className={`flex items-center gap-4 flex-1 min-w-0 ${isHeld ? 'cursor-not-allowed' : 'cursor-pointer'}`}
				title={isHeld ? t('inventory.held.disabledReason') : undefined}
			>
				<ItemCheckbox checked={selected} disabled={isHeld} onChange={onToggle} />

				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="font-medium text-gray-900">
							&euro;{centsToEuros(item.valueCents).toFixed(2)}
						</span>
						<span className="text-gray-400">·</span>
						<span className="text-gray-600">
							{t('inventory.campaignCount', { count: item.quantity })}
						</span>

						{item.isExpired && (
							<span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
								{t('inventory.expired')}
							</span>
						)}
						{isExpiringSoon && (
							<span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
								{t('inventory.expiringSoon')}
							</span>
						)}
						{isHeld
							? (
								<span className="px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 rounded">
									{t('inventory.held.badge', { held: heldQuantity, total: item.quantity })}
								</span>
							)
							: null}
					</div>

					{item.description && <div className="text-sm text-gray-500 truncate mt-0.5">{item.description}
					</div>}

					<div className="text-xs text-gray-400 mt-1">
						{t('inventory.expires', { date: expiresAt.toLocaleDateString() })}
					</div>
				</div>
			</label>

			{onSplit && (() => {
				const splitDisabledReason = selected
					? t('inventory.split.disabledWhenSelected')
					: item.availableQuantity === 0
					? t('inventory.held.disabledReason')
					: item.quantity === 1
					? t('inventory.split.disabledWhenSingle')
					: null
				const isSplitDisabled = splitDisabledReason !== null
				return (
					<Tooltip content={splitDisabledReason} disabled={!isSplitDisabled}>
						<IconButton
							variant="ghost"
							size="icon-sm"
							disabled={isSplitDisabled}
							onClick={onSplit}
							aria-label={t('inventory.split.action')}
							className={ctl(`
								text-gray-300 hover:text-brand-600 hover:bg-brand-50
								cursor-pointer disabled:cursor-not-allowed
								transition-opacity
								${
								isSplitDisabled
									? 'opacity-100'
									: 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
							}
							`)}
						>
							<SplitIcon className="size-4" aria-hidden="true" />
						</IconButton>
					</Tooltip>
				)
			})()}

			{onDelete && (
				<IconButton
					variant="ghost"
					size="icon-sm"
					disabled={selected || isHeld}
					onClick={onDelete}
					aria-label={t('inventory.delete.action')}
					title={selected
						? t('inventory.delete.disabledWhenSelected')
						: isHeld
						? t('inventory.held.disabledReason')
						: undefined}
					className={ctl(`
						text-gray-300 hover:text-red-600 hover:bg-red-50
						cursor-pointer disabled:cursor-not-allowed
						transition-opacity
						${
						selected || isHeld
							? 'opacity-100'
							: 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
					}
					`)}
				>
					<Trash2Icon className="size-4" aria-hidden="true" />
				</IconButton>
			)}
		</div>
	)
}

interface ItemCheckboxProps {
	checked: boolean
	indeterminate?: boolean
	disabled?: boolean
	onChange: () => void
}

function ItemCheckbox({ checked, indeterminate, disabled = false, onChange }: ItemCheckboxProps) {
	return (
		<Checkbox
			checked={checked}
			disabled={disabled}
			onChange={onChange}
			className={ctl(`
				w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0
				${disabled ? 'opacity-40' : ''}
				${checked || indeterminate ? 'bg-brand-600 border-brand-600' : 'border-gray-300 bg-white'}
				focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
			`)}
		>
			{checked && (
				<svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
					<path
						d="M2.5 6L5 8.5L9.5 3.5"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)}
			{indeterminate && !checked && <div className="w-2 h-0.5 bg-white rounded" />}
		</Checkbox>
	)
}
